import { SectionTitleV2 } from '../../../atoms/v2/section-title/section-title.atom';
import { LabelV2 } from '../../../atoms/v2/label/label-v2.atom';
import { CheckboxV2 } from '../../../atoms/v2/checkbox/checkbox.atom';
import React from 'react';
import * as S from './payment-methods.organism.style';
import { MetodoPagamentoVendaEnum } from '../../../../common/types/enums/metodo-pagamento.enum';
import { Trans, useTranslation } from 'react-i18next';
import { PiMoney } from 'react-icons/pi';
import { GiCheckMark } from 'react-icons/gi';
import { Checkout } from '../../../../common/types/checkout';
import { MoedaVendaEnum } from '../../../../common/types/enums/moeda-venda.enum';
import {
  MpCardSection,
  MpCardSectionRef,
} from '../../../molecules/mp-card-section/mp-card-section';
import { FormDataShape } from '../../../../common/hooks/useCheckoutController/useCheckoutController.types';
import { InputField } from '../../../molecules/input-field/input-field.molecule';
import { documentMasks } from '../../../../common/utils/document-masks';
import { StripeCardSection } from '../../../molecules/stripe-card-section/stripe-card-section';
import { StripeElementRef } from '../../../atoms/stripe-inputs/stripe-card-input/stripe-card-input';
import { PaymentMethodsClassic } from '../../../molecules/classic-checkout/payment-methods/payment-methods-classic/payment-methods-classic';
import { PaymentMethodsMinimalist } from '../../../molecules/classic-checkout/payment-methods/payment-methods-minimalist/payment-methods-minimalist';
import { PaymentMethodsIOS } from '../../../molecules/classic-checkout/payment-methods/payment-methods-ios/payment-methods-ios';
import Apple from '../../../../common/assets/Apple.png';
import { ApplePayBtn } from '../../../atoms/apple-pay-button/apple-pay-button.aom';
import { GooglePayBtn } from '../../../atoms/google-pay-button/google-pay-button.aom';

type Props = {
  selectedMethod: MetodoPagamentoVendaEnum;
  setSelectedMethod: (method: MetodoPagamentoVendaEnum) => void;
  checkout: Checkout;
  formData: FormDataShape;
  onFormDataChange: (field: keyof FormDataShape, value: string) => void;
  mpCardRef?: React.RefObject<MpCardSectionRef>;
  numberRef: React.RefObject<StripeElementRef>;
  expiryRef: React.RefObject<StripeElementRef>;
  cvcRef: React.RefObject<StripeElementRef>;
  handleStripeChange: (
    field: 'number' | 'expiry' | 'cvc',
    s: { complete: boolean; error: string | null },
  ) => void;
  applePay?: {
    available: boolean;
    onClick: () => void;
    loading: boolean;
  };
  googlePay?: {
    available: boolean;
    onClick: () => void;
    loading: boolean;
  };
  handlePlaceOrder?: () => void;
};

export const PaymentMethods: React.FC<Props> = ({
  selectedMethod,
  setSelectedMethod,
  checkout,
  formData,
  onFormDataChange,
  mpCardRef,
  numberRef,
  expiryRef,
  cvcRef,
  handleStripeChange,
  applePay,
  googlePay,
  handlePlaceOrder,
}) => {
  const { t } = useTranslation();

  return (
    <S.PaymentContainer>
      <SectionTitleV2>
        <PiMoney />
        <span>{t('payment')}</span>
      </SectionTitleV2>

      {(checkout?.checkoutConfig?.config?.checkoutData?.paymentMethods
        ?.variant === 'classic' ||
        !checkout?.checkoutConfig?.config?.checkoutData?.paymentMethods
          ?.variant) && (
        <PaymentMethodsClassic
          applePay={applePay}
          googlePay={googlePay}
          checkout={checkout}
          selectedMethod={selectedMethod}
          setSelectedMethod={setSelectedMethod}
        />
      )}

      {checkout?.checkoutConfig?.config?.checkoutData?.paymentMethods
        ?.variant === 'minimalist' && (
        <PaymentMethodsMinimalist
          applePay={applePay}
          googlePay={googlePay}
          checkout={checkout}
          selectedMethod={selectedMethod}
          setSelectedMethod={setSelectedMethod}
        />
      )}

      {checkout?.checkoutConfig?.config?.checkoutData?.paymentMethods
        ?.variant === 'ios' && (
        <PaymentMethodsIOS
          applePay={applePay}
          googlePay={googlePay}
          selectedMethod={selectedMethod}
          setSelectedMethod={setSelectedMethod}
          checkout={checkout}
        />
      )}

      {/* PIX */}
      {selectedMethod === MetodoPagamentoVendaEnum.PIX && (
        <>
          <S.PixInfo>
            <S.InfoItem>
              <GiCheckMark /> {t('immediateRelease')}
            </S.InfoItem>
            <S.InfoItem>
              <GiCheckMark /> {t('pixPaymentInstruction')}
            </S.InfoItem>
          </S.PixInfo>
          <div className="mt-2">
            <InputField
              color={
                checkout?.checkoutConfig?.config?.checkoutData?.purchaseButton
                  ?.background
              }
              id="cpf"
              label={t('document')}
              placeholder={t('fillYourDocument')}
              mask={documentMasks[checkout?.paisEnum]}
              maskChar=""
              value={formData.cpf}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                onFormDataChange('cpf', e.target.value)
              }
            />
          </div>
        </>
      )}

      {/* BOLETO */}
      {selectedMethod === MetodoPagamentoVendaEnum.BOLETO && (
        <>
          <S.PixInfo>
            <S.InfoItem>
              <GiCheckMark /> {t('availableForPayment')}
            </S.InfoItem>
            <S.InfoItem>
              <GiCheckMark /> {t('clearanceTimeTwoDays')}
            </S.InfoItem>
          </S.PixInfo>
          <div className="mt-2">
            <InputField
              color={
                checkout?.checkoutConfig?.config?.checkoutData?.purchaseButton
                  ?.background
              }
              id="cpf"
              label={t('document')}
              placeholder={t('fillYourDocument')}
              mask={documentMasks[checkout?.paisEnum]}
              maskChar=""
              value={formData.cpf}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                onFormDataChange('cpf', e.target.value)
              }
            />
          </div>
        </>
      )}

      {/* Apple Pay */}
      {selectedMethod === MetodoPagamentoVendaEnum.APPLE_PAY && (
        <S.CardForm>
          <ApplePayBtn
            onClick={applePay?.onClick}
            disabled={!applePay?.available || applePay?.loading}
          >
            {applePay?.loading ? (
              ''
            ) : (
              <Trans
                i18nKey="payWith"
                values={{ method: 'Apple Pay' }}
                components={{
                  method: <></>,
                }}
              />
            )}
            <img
              src={Apple}
              alt="Apple"
              style={{ width: 18, height: 18 }}
            />
          </ApplePayBtn>
        </S.CardForm>
      )}

      {/* Google Pay */}
      {selectedMethod === MetodoPagamentoVendaEnum.GOOGLE_PAY && (
        <S.CardForm>
          <GooglePayBtn
            onClick={googlePay?.onClick}
            disabled={!googlePay?.available || googlePay?.loading}
          >
            {googlePay?.loading ? (
              ''
            ) : (
              <Trans
                i18nKey="payWith"
                values={{ method: 'Google Pay' }}
                components={{
                  method: <></>,
                }}
              />
            )}
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
          </GooglePayBtn>
        </S.CardForm>
      )}

      {/* Amazon Pay */}
      {selectedMethod === MetodoPagamentoVendaEnum.AMAZON_PAY && (
        <S.CardForm>
          <S.AmazonPayBtn type="button" onClick={handlePlaceOrder}>
            Pagar com{' '}
            <img
              src="https://upload.wikimedia.org/wikipedia/commons/a/a9/Amazon_logo.svg"
              alt="Amazon Pay"
              style={{ height: 16 }}
            />
          </S.AmazonPayBtn>
        </S.CardForm>
      )}

      {/* CARTÃO */}
      <S.CardForm>
        <div
          aria-hidden={
            selectedMethod !== MetodoPagamentoVendaEnum.CARTAO_CREDITO
          }
          style={{
            display:
              selectedMethod === MetodoPagamentoVendaEnum.CARTAO_CREDITO
                ? 'block'
                : 'none',
          }}
        >
          {checkout?.moeda === MoedaVendaEnum.BRL && !checkout?.recorrencia ? (
            <MpCardSection
              checkout={checkout}
              data={formData}
              onChange={onFormDataChange}
              ref={mpCardRef}
            />
          ) : (
            <StripeCardSection
              checkout={checkout}
              handleStripeChange={handleStripeChange}
              expiryRef={expiryRef}
              numberRef={numberRef}
              cvcRef={cvcRef}
              data={formData}
              onChange={onFormDataChange}
            />
          )}
        </div>

        <S.SaveCardCheckbox>
          <CheckboxV2 type="checkbox" id="saveCard" />
          <LabelV2
            htmlFor="saveCard"
            style={{ marginBottom: 0, cursor: 'pointer' }}
          >
            {t('saveDataForNextPurchases')}
          </LabelV2>
        </S.SaveCardCheckbox>

        <S.SecurityNote>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
            />
          </svg>
          <span>{t('securePaymentData')}</span>
        </S.SecurityNote>
      </S.CardForm>
    </S.PaymentContainer>
  );
};
